import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search,
  Person,
  CheckCircle,
  Error,
  Refresh,
  PlayArrow,
  Info,
  Email,
  PersonAdd,
  NavigateBefore,
  NavigateNext,
  ListAlt,
  ExpandMore,
  Warning,
} from '@mui/icons-material';
import { adminMigrationService, UserInfo, UserMigrationStatus, MigrationResult, UsersReadyForMigrationResult, PaginationInfo, BatchInfo, EnhancedUserInfo, UserDataStatus } from '../../services/adminMigrationService';

const IndividualMigrationManager: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserInfo[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  const [userStatus, setUserStatus] = useState<UserMigrationStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState<MigrationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  
  // User list state
  const [usersReadyForMigration, setUsersReadyForMigration] = useState<EnhancedUserInfo[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentBatch, setCurrentBatch] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'ready' | 'not_ready' | 'all'>('ready');

  /**
   * Load users with migration status filtering
   */
  const loadUsersReadyForMigration = async (page: number = 1, status: 'ready' | 'not_ready' | 'all' = statusFilter, batch: number = currentBatch) => {
    try {
      setIsLoadingUsers(true);
      setError(null);
      
      const result = await adminMigrationService.getUsersReadyForMigration(page, 10, status, batch);
      setUsersReadyForMigration(result.users);
      setPagination(result.pagination);
      setBatchInfo(result.batch);
      setCurrentPage(page);
      setCurrentBatch(batch);
    } catch (err: any) {
      console.error('Error loading users ready for migration:', err);
      setError(err.message || 'Failed to load users ready for migration');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  /**
   * Handle page change
   */
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && pagination && newPage <= pagination.totalPages) {
      loadUsersReadyForMigration(newPage, statusFilter);
    }
  };

  /**
   * Handle status filter change
   */
  const handleStatusFilterChange = (newStatus: 'ready' | 'not_ready' | 'all') => {
    setStatusFilter(newStatus);
    setCurrentBatch(1); // Reset to first batch when changing status
    loadUsersReadyForMigration(1, newStatus, 1);
  };

  /**
   * Handle batch change
   */
  const handleBatchChange = (newBatch: number) => {
    setCurrentBatch(newBatch);
    loadUsersReadyForMigration(1, statusFilter, newBatch);
  };

  /**
   * Refresh user list after migration
   */
  const refreshUserList = async () => {
    await loadUsersReadyForMigration(currentPage);
  };

  /**
   * Search for users
   */
  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setError('Search query must be at least 2 characters');
      return;
    }

    try {
      setIsSearching(true);
      setError(null);
      setSearchResults([]);
      
      const result = await adminMigrationService.searchUsers(searchQuery);
      setSearchResults(result.users);
      
      if (result.users.length === 0) {
        setError('No users found matching your search');
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search users');
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Get user migration status
   */
  const getUserStatus = async (user: UserInfo) => {
    try {
      setIsLoadingStatus(true);
      setError(null);
      
      const status = await adminMigrationService.getUserMigrationStatus(user.uid);
      setUserStatus(status);
      setSelectedUser(user);
    } catch (err: any) {
      console.error('Error getting user status:', err);
      setError(err.message || 'Failed to get user status');
    } finally {
      setIsLoadingStatus(false);
    }
  };

  /**
   * Migrate user
   */
  const migrateUser = async () => {
    if (!selectedUser) return;

    try {
      setIsMigrating(true);
      setError(null);
      setSuccess(null);
      setMigrationResult(null);

      const result = await adminMigrationService.migrateUser(selectedUser.uid);
      setMigrationResult(result);
      
      if (result.success) {
        setSuccess(result.message);
        // Refresh user status
        await getUserStatus(selectedUser);
        // Refresh user list to remove migrated user
        await refreshUserList();
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      console.error('Migration error:', err);
      setError(err.message || 'Failed to migrate user');
    } finally {
      setIsMigrating(false);
    }
  };

  /**
   * Clear all states
   */
  const clearStates = () => {
    setSearchResults([]);
    setSelectedUser(null);
    setUserStatus(null);
    setMigrationResult(null);
    setError(null);
    setSuccess(null);
  };

  /**
   * Handle search input change
   */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    if (event.target.value.length < 2) {
      clearStates();
    }
  };

  // Load users ready for migration on component mount
  useEffect(() => {
    loadUsersReadyForMigration(1);
  }, []);

  /**
   * Get status chip for user
   */
  const getStatusChip = (user: EnhancedUserInfo) => {
    if (user.isMigrated) {
      return <Chip icon={<CheckCircle />} label="Migrated" color="success" size="small" />;
    } else if (user.dataStatus.hasData) {
      return <Chip icon={<PersonAdd />} label="Ready to Migrate" color="primary" size="small" />;
    } else {
      return <Chip icon={<Warning />} label="Not Ready" color="warning" size="small" />;
    }
  };

  /**
   * Handle search on Enter key
   */
  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  /**
   * Get migration status chip
   */
  const getMigrationStatusChip = (status: UserMigrationStatus | null) => {
    if (!status) return null;

    if (status.migrationStatus?.migrated) {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Migrated"
          color="success"
          size="small"
        />
      );
    } else if (status.userInfo.hasData) {
      return (
        <Chip
          icon={<PersonAdd />}
          label="Ready to Migrate"
          color="primary"
          size="small"
        />
      );
    } else {
      return (
        <Chip
          icon={<Error />}
          label="Insufficient Data"
          color="warning"
          size="small"
        />
      );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Individual User Migration
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Search for specific users and migrate their eligibility data individually.
        This allows for targeted migration of users who need immediate access to the new system.
      </Typography>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Search Users
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Search by email or name"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              placeholder="Enter email address or display name..."
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isSearching || searchQuery.length < 2}
              startIcon={isSearching ? <CircularProgress size={20} /> : <Search />}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </Box>

          {searchQuery.length > 0 && searchQuery.length < 2 && (
            <Alert severity="info">
              Enter at least 2 characters to search
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Users Ready for Migration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              User Migration Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => handleStatusFilterChange(e.target.value as 'ready' | 'not_ready' | 'all')}
                >
                  <MenuItem value="ready">Ready to Migrate</MenuItem>
                  <MenuItem value="not_ready">Not Ready</MenuItem>
                  <MenuItem value="all">All Users</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => loadUsersReadyForMigration(currentPage, statusFilter)}
                disabled={isLoadingUsers}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {pagination && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing {usersReadyForMigration.length} of {pagination.totalUsers} users ({statusFilter === 'ready' ? 'ready for migration' : statusFilter === 'not_ready' ? 'not ready for migration' : 'all users'})
            </Typography>
          )}

          {isLoadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : usersReadyForMigration.length > 0 ? (
            <>
              <List>
                {usersReadyForMigration.map((user, index) => (
                  <React.Fragment key={user.uid}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Person color="primary" />
                          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                            {user.displayName || 'No Display Name'}
                          </Typography>
                          {getStatusChip(user)}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Email fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {user.email || 'No email'}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            UID: {user.uid} • Created: {new Date(user.createdAt || '').toLocaleDateString()}
                          </Typography>
                          
                          {/* Data Status Details */}
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Data Status:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                              <Chip 
                                label={`Profile: ${user.dataStatus.details.hasProfile ? '✓' : '✗'}`} 
                                color={user.dataStatus.details.hasProfile ? 'success' : 'error'} 
                                size="small" 
                              />
                              <Chip 
                                label={`Courses: ${user.dataStatus.details.courseCount}`} 
                                color={user.dataStatus.details.hasCourses ? 'success' : 'error'} 
                                size="small" 
                              />
                              <Chip 
                                label={`Grades: ${user.dataStatus.details.gradeCount}`} 
                                color={user.dataStatus.details.hasGrades ? 'success' : 'error'} 
                                size="small" 
                              />
                            </Box>
                            
                            {/* Reasons */}
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Status:</strong> {user.dataStatus.reasons.join(', ')}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Action Button */}
                        {!user.isMigrated && user.dataStatus.hasData && (
                          <Button
                            variant="contained"
                            startIcon={<PlayArrow />}
                            onClick={() => getUserStatus(user)}
                            disabled={isLoadingStatus}
                            size="small"
                          >
                            {isLoadingStatus && selectedUser?.uid === user.uid ? 'Loading...' : 'Migrate User'}
                          </Button>
                        )}
                      </AccordionDetails>
                    </Accordion>
                    {index < usersReadyForMigration.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<NavigateBefore />}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!pagination.hasPreviousPage || isLoadingUsers}
                  >
                    Previous
                  </Button>
                  
                  <Typography variant="body2" color="text.secondary">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    endIcon={<NavigateNext />}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.hasNextPage || isLoadingUsers}
                  >
                    Next
                  </Button>
                </Box>
              )}

              {/* Batch Navigation */}
              {batchInfo && (batchInfo.hasNextBatch || batchInfo.hasPreviousBatch) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" color="text.secondary">
                    Batch {batchInfo.currentBatch} (50 users per batch)
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    startIcon={<NavigateBefore />}
                    onClick={() => handleBatchChange(currentBatch - 1)}
                    disabled={!batchInfo.hasPreviousBatch || isLoadingUsers}
                    size="small"
                  >
                    Previous Batch
                  </Button>
                  
                  <Button
                    variant="outlined"
                    endIcon={<NavigateNext />}
                    onClick={() => handleBatchChange(currentBatch + 1)}
                    disabled={!batchInfo.hasNextBatch || isLoadingUsers}
                    size="small"
                  >
                    Next Batch
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Alert severity="info">
              {statusFilter === 'ready' && 'No users are currently ready for migration. All users with sufficient data may already be migrated.'}
              {statusFilter === 'not_ready' && 'No users found that are not ready for migration. All users may have sufficient data.'}
              {statusFilter === 'all' && 'No users found. This may indicate an issue with user data retrieval.'}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Search Results ({searchResults.length} found)
            </Typography>
            
            <List>
              {searchResults.map((user, index) => (
                <React.Fragment key={user.uid}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person color="primary" />
                          <Typography variant="subtitle1">
                            {user.displayName || 'No Display Name'}
                          </Typography>
                          {getMigrationStatusChip(userStatus?.userInfo.uid === user.uid ? userStatus : null)}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Email fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {user.email || 'No email'}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            UID: {user.uid}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="View Details & Migrate">
                        <IconButton
                          edge="end"
                          onClick={() => getUserStatus(user)}
                          disabled={isLoadingStatus}
                        >
                          {isLoadingStatus && selectedUser?.uid === user.uid ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Info />
                          )}
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < searchResults.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* User Details & Migration */}
      {selectedUser && userStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Details & Migration
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    User Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {userStatus.userInfo.displayName || 'No Display Name'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {userStatus.userInfo.email || 'No email'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>UID:</strong> {userStatus.userInfo.uid}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Migration Status
                  </Typography>
                  {userStatus.migrationStatus?.migrated ? (
                    <Box>
                      <Typography variant="body2" color="success.main">
                        <strong>Status:</strong> Migrated
                      </Typography>
                      <Typography variant="body2">
                        <strong>Migrated At:</strong> {new Date(userStatus.migrationStatus.migratedAt || '').toLocaleString()}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Programs:</strong> {userStatus.migrationStatus.programCount}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Source:</strong> {userStatus.migrationStatus.source}
                      </Typography>
                    </Box>
                  ) : userStatus.userInfo.hasData ? (
                    <Typography variant="body2" color="primary.main">
                      <strong>Status:</strong> Ready to migrate
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="warning.main">
                      <strong>Status:</strong> Insufficient data for migration
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Migration Actions */}
            {userStatus.userInfo.hasData && !userStatus.migrationStatus?.migrated && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={isMigrating ? <CircularProgress size={20} /> : <PlayArrow />}
                  onClick={migrateUser}
                  disabled={isMigrating}
                >
                  {isMigrating ? 'Migrating...' : 'Migrate User'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => getUserStatus(selectedUser)}
                  disabled={isLoadingStatus}
                >
                  Refresh Status
                </Button>
              </Box>
            )}

            {userStatus.migrationStatus?.migrated && (
              <Alert severity="success">
                This user has already been migrated and is using the new eligibility system.
              </Alert>
            )}

            {!userStatus.userInfo.hasData && (
              <Alert severity="warning">
                This user has insufficient data (missing courses or grade information) and cannot be migrated.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Error:</strong> {error}
          </Typography>
        </Alert>
      )}

      {/* Success Display */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Success:</strong> {success}
          </Typography>
        </Alert>
      )}

      {/* Migration Result */}
      {migrationResult && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Migration Result
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {migrationResult.success ? (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  ) : (
                    <Error color="error" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="body2">
                    <strong>Status:</strong> {migrationResult.success ? 'Success' : 'Failed'}
                  </Typography>
                </Box>
              </Grid>
              
              {migrationResult.migrationInfo?.programCount && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Programs Migrated:</strong> {migrationResult.migrationInfo.programCount}
                  </Typography>
                </Grid>
              )}
            </Grid>
            
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Message:</strong> {migrationResult.message}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default IndividualMigrationManager;
